import { useEffect, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ButtonVanilla,
  CloseIcon,
  Modal,
  Typography,
  useMediaQuery
} from '@ag1/ui';
import type { ButtonVanillaVariant } from '@ag1/ui/components/ButtonVanilla/ButtonVanilla';
import type { NbaOfferPopup as NbaOfferPopupProps, SanityKeyed } from 'types/studio/studio';
import { LocaleEnum } from 'schema/locale';
import { sanityFetch } from 'sanity-client';
import { useHasPouchOrPackSubscription } from 'processes/ShopListProducts/hooks/useHasPouchOrPackSubscription';
import { useDeferredSessionValue } from 'appLayer/auth';
import { useLocale } from 'shared/hooks/useLocale';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { CustomPortableText } from 'shared/studio/PortableText';
import { StudioBaseIconV2 } from 'shared/studio/StudioBaseIcon/StudioBaseIcon';
import { safeLocalStorage } from 'shared/utils/safeLocalStorage';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import type { CommonSectionProps } from '../types';
import * as styles from './NbaOfferPopup.css';

const DISMISSED_NBA_10CT_OFFER_KEY = 'NBA_10ct_offer';
const DISMISSED_NBA_15CT_OFFER_KEY = 'NBA_15ct_offer';

type Props = SanityKeyed<NbaOfferPopupProps>;

export const NbaOfferPopup = (_props: CommonSectionProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [modalProps1, setModalProps1] = useState<Props | null>(null);
  const [modalProps2, setModalProps2] = useState<Props | null>(null);
  const [activeModalProps, setActiveModalProps] = useState<Props | null>(modalProps1);
  const { hasActiveSubscriptions } = useHasPouchOrPackSubscription();
  const aboveLarge = useMediaQuery('l');
  const session = useDeferredSessionValue();
  const customer = session?.customer;
  const locale = useLocale();
  // We only want to show NbaOfferPopup in EU/UK
  const language = locale === LocaleEnum.enEU ? LocaleEnum.enEU : LocaleEnum.enUK;
  const localStorageKey = customer ? DISMISSED_NBA_15CT_OFFER_KEY : DISMISSED_NBA_10CT_OFFER_KEY;

  const handleClose = () => {
    setIsOpen(false);
    safeLocalStorage.setItem(localStorageKey, 'true');
  };

  useEffect(() => {
    if (hasActiveSubscriptions) setActiveModalProps(null);
    else setActiveModalProps(customer ? modalProps2 : modalProps1);
  }, [hasActiveSubscriptions, customer, modalProps1, modalProps2]);

  useEffect(() => {
    const hasDismissedOffer = safeLocalStorage.getItem(localStorageKey);
    if (hasDismissedOffer) {
      setIsOpen(false);
      return;
    }

    const fetchMatchingNbaOfferPopup = async () => {
      const query = `*[_type == "sharedSection" && language == "${language}" && title match "NBA Offer"]{ ...sections[0] }`;

      try {
        const sections = await sanityFetch(query);
        setModalProps1(sections[0]);
        setModalProps2(sections[1]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch matching nbaOfferPopup:', error);
      }
    };

    fetchMatchingNbaOfferPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!activeModalProps) return null;

  const { baseImage, badgeText, badgeIcon, headline, text, baseButtonLink } = activeModalProps;

  // ButtonVanilla props
  const href = getSanityLinkUrl(baseButtonLink?.btnLink, locale);
  const variant = baseButtonLink?.variant ?? '';
  const isButtonVanillaVariant = (variant: string): variant is ButtonVanillaVariant =>
    [
      'primary',
      'secondary',
      'tertiary',
      'link',
      'terminal',
      'goGreen',
      'fuchsia',
      'primaryOnDark',
      'tertiaryOnDark',
      'linkOnDark'
    ].includes(variant);
  const iconPosition = baseButtonLink?.iconOptions?.position;
  let icon = null;
  if (baseButtonLink?.showIcon) {
    if (iconPosition === 'left') {
      icon = <ArrowLeftIcon size="24" />;
    } else if (iconPosition === 'right') {
      icon = <ArrowRightIcon size="24" />;
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      mode="center"
      overlayClassName={styles.overlay}
      className={styles.modal}>
      <div className={styles.modalInner}>
        <ButtonVanilla
          onClick={handleClose}
          className={styles.closeButton}
          variant="primary"
          size="XS"
          shape="round"
          isIconOnly
          iconPosition="right"
          icon={<CloseIcon size={20} />}
        />
        {baseImage?.image && (
          <StudioBaseImageV2
            image={baseImage.image}
            mobileImage={baseImage.mobileImage}
            tabletImage={baseImage.tabletImage}
            className={styles.image}
          />
        )}
        <div className={styles.content}>
          {badgeText && (
            <div className={styles.badgeWrapper}>
              {badgeIcon?.asset && (
                <StudioBaseIconV2 icon={badgeIcon} className={styles.badgeIcon} />
              )}
              <Typography variant="bodyMono" size="2" noPadding className={styles.badgeText}>
                {badgeText}
              </Typography>
            </div>
          )}
          <Typography variant="headlineSans" size="2" noPadding className={styles.headline}>
            <CustomPortableText value={headline} tag="h1" />
          </Typography>
          <Typography variant="body" size="1" noPadding className={styles.text}>
            <CustomPortableText value={text} />
          </Typography>
          {baseButtonLink?.portableText && (
            <ButtonVanilla
              className={styles.button}
              href={href}
              variant={isButtonVanillaVariant(variant) ? variant : 'primary'}
              size={aboveLarge ? 'L' : 'M'}
              shape="round"
              iconPosition={iconPosition}
              icon={icon}>
              <CustomPortableText value={baseButtonLink.portableText} />
            </ButtonVanilla>
          )}
        </div>
      </div>
    </Modal>
  );
};
